<script setup lang="ts">
  import { useModal } from '@/composition/use-modal';
  import ModalsStockItems from '../stock/ModalsStockItems.vue';
  import { IStockItem } from '@/interfaces/IStockItem';
  import { computed, inject, onMounted, Ref } from 'vue';
  import { IStockRecipe } from '@/interfaces/IStockRecipe';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import { useUi } from '@/composition/use-ui';
  import { AlertType } from '@/interfaces/IAlert';
  import ControlNumberInput from '@/components/control/ControlNumberInput.vue';
  import { IOption } from '@/interfaces/IOption';
  import { StockRecipesService } from '@/api/services/stock-recipes.service';
  import ModalsItemStockSection from '../item/ModalsItemStockSection.vue';
  import { ref, reactive } from 'vue';
  import { IOptionItem } from '@/interfaces/IOptionItem';
  import { CustomError } from '@/exceptions/custom.exception';

  const modals = useModal();
  const restaurantStore = useRestaurantStore();
  const ui = useUi();

  const optionToEdit = inject<Ref<IOption>>('optionToEdit');
  const stockRecipes = ref<IStockRecipe[]>([]);

  const editionData = reactive({
    editedIds: new Set<number>(),
    deletedIds: new Set<number>(),
  });

  async function loadStockRecipes() {
    if (!optionToEdit.value?.id) return;

    try {
      const recipes = await StockRecipesService.findByOptionId(
        restaurantStore.restaurant.id,
        restaurantStore.selectedBranch.id,
        optionToEdit.value.id
      );
      stockRecipes.value = recipes;
    } catch (error) {
      ui.serverError(error, 'Error al cargar los componentes de stock');
    }
  }

  onMounted(() => {
    loadStockRecipes();
  });

  function openStockItemsModal(optionItem: IOptionItem) {
    modals.show(
      ModalsStockItems,
      {},
      {
        name: 'stock-items',
        adaptive: true,
        classes: 'dialog--height-limit',
        height: 'auto',
        scrollable: true,
        maxWidth: window.innerWidth * 0.95,
        width: 600,
        clickToClose: true,
      },
      {
        chooseItem: (stockItem: IStockItem) => {
          modals.hide('stock-items');
          addStockItem(stockItem, optionItem);
        },
      }
    );
  }

  function addStockItem(stockItem: IStockItem, optionItem: IOptionItem) {
    if (
      stockRecipes.value?.find(
        (item) =>
          item.stockItemId === stockItem.id &&
          item.optionItemId === optionItem.id
      )
    ) {
      ui.showToast(AlertType.Error, 'Este componente de stock ya fue agregado');
      return;
    }

    stockRecipes.value = [
      ...stockRecipes.value,
      {
        id: -Math.floor(Math.random() * 10000000),
        optionId: optionToEdit.value.id,
        restaurantId: restaurantStore.restaurant.id,
        branchId: restaurantStore.selectedBranch.id,
        stockItemId: stockItem.id,
        stockItem,
        quantity: 1,
        itemId: null,
        optionItemId: optionItem.id,
      },
    ];
  }

  function updateStockRecipeQuantity({
    stockRecipe,
    quantity,
  }: {
    stockRecipe: IStockRecipe;
    quantity: number;
  }) {
    stockRecipe.quantity = quantity;

    if (stockRecipe.id > 0) {
      editionData.editedIds.add(stockRecipe.id);
    }
  }

  function deleteStockRecipe(stockRecipe: IStockRecipe) {
    stockRecipes.value = stockRecipes.value.filter(
      (item) => item.id !== stockRecipe.id
    );

    if (stockRecipe.id > 0) {
      editionData.deletedIds.add(stockRecipe.id);
      editionData.editedIds.delete(stockRecipe.id);
    }
  }

  async function save(option: IOption) {
    const saveData = {
      recipesToCreate: [],
      recipesToUpdate: [],
    };

    for (const stockRecipe of stockRecipes.value) {
      if (editionData.editedIds.has(stockRecipe.id)) {
        saveData.recipesToUpdate.push(stockRecipe);
      } else if (stockRecipe.id < 0) {
        saveData.recipesToCreate.push(stockRecipe);
      }
    }

    try {
      await StockRecipesService.massUpdate({
        restaurantId: restaurantStore.restaurant.id,
        branchId: restaurantStore.selectedBranch.id,
        toAdd: saveData.recipesToCreate,
        toUpdate: saveData.recipesToUpdate,
        toDelete: Array.from(editionData.deletedIds),
      });
    } catch (e) {
      ui.serverError(e, 'Error al guardar los componentes de stock');
      throw new CustomError('Error al guardar los componentes de stock');
    }
  }

  // Expose save method to parent
  defineExpose({
    save,
  });
</script>

<template>
  <div class="modal-option-stock">
    <template v-if="!optionToEdit?.id">
      <v-alert type="info" class="cannot-edit-message" dense>
        Guarde la opción y vuelva a abrir esta pantalla de edición para poder
        agregar componentes de stock.
      </v-alert>
    </template>
    <template v-else>
      <template v-for="optionItem in optionToEdit.optionItems">
        <ModalsItemStockSection
          :key="optionItem.id"
          :title="`Componentes / ingredientes de opción '${optionItem.title}'`"
          :stockRecipes="
            stockRecipes.filter(
              (recipe) => recipe.optionItemId === optionItem.id
            )
          "
          :optionItem="optionItem"
          @updateQuantity="updateStockRecipeQuantity"
          @delete="deleteStockRecipe"
          @showAdd="({ optionItem }) => openStockItemsModal(optionItem)"
        />
        <div class="divider"></div>
      </template>
    </template>
  </div>
</template>

<style scoped lang="scss">
  .modal-option-stock {
    @apply tw-flex tw-flex-col tw-space-y-4 tw-items-start tw-pt-4 tw-pb-2;

    .divider {
      @apply tw-w-full tw-border-b tw-border-gray-300 tw-border-dashed tw-mx-4;
    }

    .cannot-edit-message {
      @apply tw-w-full;
    }
  }
</style>
